import { Autocomplete, TextField, debounce } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const useSetupService = () => {
  const [service, setService] = useState(null)
  const [placeService, setPlaceService] = useState(null)

  useEffect(() => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.SUP_MAPS_API_KEY}&libraries=places&callback=initMap`
    script.async = true
    script.id = 'google-script'

    const div = document.createElement('div')
    document.body.appendChild(div)

    document.body.appendChild(script)

    script.addEventListener('load', () => {
      setService(new google.maps.places.AutocompleteService())
      setPlaceService(new google.maps.places.PlacesService(div))
    })
    return () => {
      document.body.removeChild(script)
    }
  }, [])

  return [service, placeService]
}

const AddressAutocomplete = ({
  value,
  setValue,
  handleBulkLocationChange,
  isBusiness,
}) => {
  const [service, placeService] = useSetupService()

  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)

  const [options, setOptions] = useState([])

  const getOptionLabel = (option) => option.description || option

  const fetchData = useCallback(
    debounce((input) => {
      try {
        if (input)
          service.getPlacePredictions(
            {
              input,
              componentRestrictions: { country: 'us' },
              fields: ['address_components'],
              types: ['address'],
            },
            (predictions, status) => {
              setLoading(false)
              if (
                status != google.maps.places.PlacesServiceStatus.OK ||
                !predictions
              ) {
                setError(true)
                return
              }

              setOptions(predictions)
            }
          )
      } catch (e) {
        setLoading(false)
      }
    }, 1000),
    [service]
  )

  const onInput = (val) => {
    setValue(val)

    if (!val) {
      setLoading(false)
      return
    }
    setLoading(true)
    fetchData(val)
  }

  const handleSelect = (_e, val) => {
    placeService.getDetails(
      {
        placeId: val.place_id,
        fields: ['address_components'],
      },
      (result, status) => {
        const components = result.address_components

        const findByType = (type) => {
          const found = components.find((x) => x.types.includes(type))

          const val =
            type === 'administrative_area_level_1'
              ? found?.short_name
              : found?.long_name
          return val || ''
        }

        const streetNumber = findByType('street_number')

        const route = findByType('route')

        const streetAddress =
          streetNumber + (streetNumber && route ? ' ' : '') + route
        const city = findByType('locality')
        const stateOrProvince = findByType('administrative_area_level_1')
        const zipCode = findByType('postal_code')

        handleBulkLocationChange({
          streetAddress,
          city,
          stateOrProvince,
          zipCode,
        })
      }
    )
  }

  return (
    <>
      <Autocomplete
        freeSolo
        value={value || ''}
        loading={loading}
        options={loading ? [] : [...options]}
        // disable automatic filtering
        filterOptions={(options) => options}
        onChange={handleSelect}
        renderInput={(params) => (
          <TextField
            value={value || ''}
            size='medium'
            margin='dense'
            fullWidth
            label={`${isBusiness ? 'Primary Business ' : ''}Address`}
            onChange={(e) => {
              onInput(e.target.value)
            }}
            {...params}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'one-time-code', // this disables chrome autofill
            }}
          />
        )}
        getOptionLabel={getOptionLabel}
      />
    </>
  )
}

export default AddressAutocomplete
